import {Injectable} from '@angular/core';
import {HttpClient, HttpContext} from '@angular/common/http';
import {environment} from '../environments/environment';
import {SKIP_ERROR_INTERCEPTOR} from '../app/interceptors/error.interceptor';
import {Place} from '../models/place';

const API = environment.API

@Injectable({
  providedIn: 'root',
})
export class PlaceService {

  constructor(private http: HttpClient) {
  }

  createPlace(name: string, phoneNumber: string, neighborhood: string, address: string, address2: string, city: string, country: string, state: string, zipcode: string, latitude: number, longitude: number, region: string) {
    const body = {name, phoneNumber, neighborhood, address, address2, city, country, state, zipcode, latitude, longitude, region};
    return this.http.post(`${API}/api/places`, body);
  }

  getById(placeId: string, startDate: string, showPermanentSchedule: boolean) {
    const params = {startDate, showPermanentSchedule};
    return this.http.get<Place>(`${API}/api/places/${placeId}`, {params});
  }

  getBySlug(slug: string) {
    return this.http.get<Place>(`${API}/api/places/${slug}/slug`);
  }

  getByInviteCode(inviteCode: string) {
    return this.http.get<Place>(`${API}/api/places/${inviteCode}/invitecode`);
  }

  findPlaces(showInvisible: boolean, showNoSeats: boolean, limit = 1000, skip = 0) {
    const params = {showInvisible, showNoSeats, limit, skip};
    return this.http.get<Place[]>(`${API}/api/places`, {params});
  }

  findByLeader(leaderId: string, showInvisible: boolean, showNoSeats: boolean, addRegions: boolean, limit = 1000, skip = 0) {
    const params = {showInvisible, showNoSeats, addRegions, limit, skip};
    return this.http.get<Place[]>(`${API}/api/places/leader/${leaderId}`, {params});
  }

  findByBlocked(userId: string, showInvisible: boolean, showNoSeats: boolean, addRegions: boolean, limit = 1000, skip = 0) {
    const params = {showInvisible, showNoSeats, addRegions, limit, skip};
    return this.http.get<Place[]>(`${API}/api/places/blocked/${userId}`, {params});
  }

  findByRegion(regionSlug: string, showInvisible = false, showNoSeats = false, specialSort = false, limit = 1000, skip = 0) {
    const params = {showInvisible, showNoSeats, specialSort, limit, skip};
    return this.http.get<Place[]>(`${API}/api/places/region/${regionSlug}`, {params});
  }

  findByGeolocation(latitude: number, longitude: number, radius: number, showInvisible = false, showNoSeats = false, limit = 1000, skip = 0) {
    const params = {latitude, longitude, radius, showInvisible, showNoSeats, limit, skip};
    return this.http.get<Place[]>(`${API}/api/places/geolocation`, {params});
  }

  findByNearby(placeId: string, showInvisible: boolean, showNoSeats: boolean, limit = 1000, skip = 0) {
    const params = {showInvisible, showNoSeats, limit, skip};
    return this.http.get<Place[]>(`${API}/api/places/nearby/${placeId}`, {params});
  }

  findByUser(limit = 1000, skip = 0) {
    const params = {limit, skip}
    return this.http.get<Place[]>(`${API}/api/places/user`, {params});
  }

  search(search: string, showInvisible: boolean, showNoSeats: boolean, limit = 1000, skip = 0) {
    const params = {search, showInvisible, showNoSeats, limit, skip};
    return this.http.get(`${API}/api/places/search`, {params});
  }

  like(placeId: string) {
    return this.http.put(`${API}/api/places/${placeId}/like`, {}, {
      context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true)
    });
  }

  unlike(placeId: string) {
    return this.http.put(`${API}/api/places/${placeId}/unlike`, {}, {
      context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true)
    });
  }

  updateDetail(
    placeId: string,
    name: string,
    description: string,
    descriptionHtml: string,
    phoneNumber: string,
    neighborhood: string,
    address: string,
    address2: string,
    city: string,
    country: string,
    state: string,
    zipcode: string,
    latitude: number,
    longitude: number,
    regions: string[],
    seating: string,
    importantFacts: string[],
    words: string[],
  ) {
    const body = {
      name,
      description,
      descriptionHtml,
      phoneNumber,
      neighborhood,
      address,
      address2,
      city,
      country,
      state,
      zipcode,
      latitude,
      longitude,
      regions,
      seating,
      importantFacts,
      words,
    };
    return this.http.put(`${API}/api/places/${placeId}/detail`, body);
  }

  updateHours(placeId: string, timezone: string, hours: any, verifiedHours: any) {
    const body = {timezone, hours, verifiedHours};
    return this.http.put(`${API}/api/places/${placeId}/hours`, body);
  }

  updateAmenities(placeId: string, amenities: any) {
    const body = {amenities};
    return this.http.put(`${API}/api/places/${placeId}/amenities`, body);
  }

  updateImages(placeId: string, smallImageIds: string[], mediumImageIds: string[], largeImageIds: string[]) {
    const body = {smallImageIds, mediumImageIds, largeImageIds};
    return this.http.put(`${API}/api/places/${placeId}/images`, body);
  }

  updateLeaders(placeId: string, leaderIds: string[]) {
    const body = {leaderIds};
    return this.http.put(`${API}/api/places/${placeId}/leaders`, body);
  }

  updateOptions(
    placeId: string,
    guestsAllowed: boolean,
    nearbyPlacesAllowed: boolean,
    visitEmailAllowed: boolean,
    holdEmailAllowed: boolean,
    toursAllowed: boolean,
    extendedHoursAllowed: boolean,
    extendedVerifiedHoursAllowed: boolean,
    nonVerifiedUnlockAllowed: boolean,
    extendedHoursRestricted: boolean,
    profilePictureRequired: boolean,
    freeTrialRestricted: boolean,
    emailSubjectDetail: boolean,
    useCoordinates: boolean,
    blockVisible: boolean,
  ) {
    const body = {
      guestsAllowed,
      nearbyPlacesAllowed,
      visitEmailAllowed,
      holdEmailAllowed,
      toursAllowed,
      extendedHoursAllowed,
      extendedVerifiedHoursAllowed,
      nonVerifiedUnlockAllowed,
      extendedHoursRestricted,
      profilePictureRequired,
      freeTrialRestricted,
      emailSubjectDetail,
      useCoordinates,
      blockVisible
    };
    return this.http.put(`${API}/api/places/${placeId}/options`, body);
  }

  updateVisibility(placeId: string, visible: boolean, note: string | null = null, nextVisible: string | null = null) {
    const body = {visible, note, nextVisible};
    return this.http.put(`${API}/api/places/${placeId}/visibility`, body);
  }

  updateExclusivity(placeId: string, exclusive: boolean) {
    const body = {exclusive};
    return this.http.put(`${API}/api/places/${placeId}/exclusivity`, body);
  }

  sendWelcome(placeId: string) {
    return this.http.put(`${API}/api/places/${placeId}/welcome`, {});
  }

  updateHoliday(placeId: string, sunday: any, monday: any, tuesday: any, wednesday: any, thursday: any, friday: any, saturday: any) {
    const body = {sunday, monday, tuesday, wednesday, thursday, friday, saturday};
    return this.http.put(`${API}/api/places/${placeId}/holiday`, body);
  }

  deactivate(placeId: string) {
    return this.http.put(`${API}/api/places/${placeId}/deactivate`, {});
  }
}
